import React, { useState, useEffect } from 'react';
import FormTabs from './FormTabs';
import PaymentModalView from './PaymentModalView';
import FormSecurityNotice from './FormSecurityNotice';
import { CrossIcon, RentIcon } from './Icons';

const RentForm = ({ domain, onClose, onSwitchToBuy, onSwitchToForward }) => {
    const [formData, setFormData] = useState({
        aRecord: '',
        rentalPeriod: 1
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [showPaymentOptions, setShowPaymentOptions] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

    // Handle closing the modal with Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        // Add event listeners
        document.addEventListener('keydown', handleEscape);

        // Cleanup event listeners
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    // Reset state only if user switched to a completely different domain
    const prevDomainIdRef = React.useRef(domain?.id);
    useEffect(() => {
        if (prevDomainIdRef.current !== domain?.id) {
            prevDomainIdRef.current = domain?.id;
            setFormData({ aRecord: '', rentalPeriod: 1 });
            setShowPaymentOptions(false);
            setInvoiceData(null);
            setSubmitSuccess(false);
            setSubmitError('');
        }
    }, [domain?.id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const calculatePrice = () => {
        const months = parseInt(formData.rentalPeriod);
        const basePrice = domain.pricerent || Math.ceil((domain.pricebuy || 0) * 0.05);

        // Progressive discount: domain.discount_rent is the max discount applied at 12 months (rounded to tenths)
        const maxDiscount = domain.discount_rent !== undefined && domain.discount_rent !== null ? Number(domain.discount_rent) : 0;
        const rawDiscountPct = maxDiscount > 0 ? ((months - 1) / 11) * maxDiscount : 0;
        const discountPercent = Math.round(rawDiscountPct * 10) / 10;
        const total = (basePrice * months) * (1 - discountPercent / 100);

        return Math.round(total * 100) / 100;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsGeneratingInvoice(true);
        setIsSubmitting(true);
        setSubmitError('');
        setSubmitSuccess(false);

        try {
            const userData = JSON.parse(localStorage.getItem('telegramUser') || '{}');
            const res = await fetch('/api/create-crypto-invoice', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'rent',
                    domainId: domain?.id,
                    months: formData.rentalPeriod,
                    destination: formData.aRecord || null,
                    userId: userData?.id || 1
                })
            });
            const data = await res.json();
            if (data.success && data.invoice) {
                setInvoiceData(data.invoice);
                setShowPaymentOptions(true);
            } else {
                setSubmitError(data.error || 'Failed to generate rental invoice.');
            }
        } catch (error) {
            console.error('Error generating invoice:', error);
            setSubmitError('Failed to generate rental invoice. Please try again.');
        } finally {
            setIsGeneratingInvoice(false);
            setIsSubmitting(false);
        }
    };

    const basePrice = Number(domain.pricerent) || 0;
    const currentPrice = calculatePrice();

    const rentUntil = domain?.rent_until ? new Date(domain.rent_until) : null;
    const forwardUntil = domain?.forward_until ? new Date(domain.forward_until) : null;
    const now = new Date();
    
    let busyUntil = null;
    if (rentUntil && rentUntil > now) busyUntil = rentUntil;
    if (forwardUntil && forwardUntil > now) {
        if (!busyUntil || forwardUntil > busyUntil) busyUntil = forwardUntil;
    }

    const hasBuy = Boolean(domain?.pricebuy && Number(domain.pricebuy) > 0);
    const hasForward = Boolean(domain?.pricefw && Number(domain.pricefw) > 0);
    const canSwitchToBuy = hasBuy ? onSwitchToBuy : undefined;
    const canSwitchToForward = hasForward ? onSwitchToForward : undefined;

    return (
        <div className="relative max-w-lg md:max-w-xl w-full animate-modal-strict">
            {/* Floating Domain Title and Close button */}
            <div className="flex items-center justify-between gap-2.5 sm:gap-3 mb-2.5 px-1 sm:px-2">
                {domain?.name ? (
                    <div className="flex items-center gap-2 min-w-0 max-w-[calc(100%-48px)]">
                        <span className="text-[10px] sm:text-xs font-bold tracking-wider uppercase text-neutral-400 bg-white/[0.08] border border-white/[0.12] px-2 py-0.5 rounded-md shrink-0">Order</span>
                        <span className="text-base sm:text-xl font-bold font-display text-white truncate domain-spotlight-text">
                            {domain.name}
                        </span>
                    </div>
                ) : (
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-purple-500/15 border border-purple-400/40 text-purple-300 text-xs font-bold uppercase tracking-wider">
                        <RentIcon className="w-3.5 h-3.5" />
                        <span>Domain Rental</span>
                    </div>
                )}

                {onClose && (
                    <button 
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-neutral-900/90 hover:bg-neutral-800 text-neutral-400 hover:text-white border border-white/[0.14] hover:border-white/[0.3] flex items-center justify-center transition-all duration-200 active:scale-90 shadow-lg cursor-pointer group shrink-0"
                        title="Close"
                        aria-label="Close modal"
                    >
                        <CrossIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-200 group-hover:rotate-90 group-hover:scale-110" />
                    </button>
                )}
            </div>

            {/* Bookmark tabs sticking out of the modal window */}
            <div className="relative z-30">
                <FormTabs 
                    activeTab="rent" 
                    onSwitchToBuy={canSwitchToBuy} 
                    onSwitchToRent={undefined} 
                    onSwitchToForward={canSwitchToForward} 
                />
            </div>

            {/* Ambient floating glow behind the modal */}
            <div className="absolute -inset-3 top-16 bg-gradient-to-b from-white/[0.08] via-purple-500/[0.04] to-transparent rounded-[32px] blur-2xl -z-10 pointer-events-none opacity-80" />

            <div className="bg-neutral-950/95 backdrop-blur-3xl rounded-3xl border border-white/[0.16] ring-1 ring-white/[0.08] max-h-[85vh] sm:max-h-[82vh] overflow-y-auto modal-scrollbar shadow-[0_30px_90px_rgba(0,0,0,0.95),0_10px_30px_rgba(0,0,0,0.7),inset_0_1px_1px_rgba(255,255,255,0.22)] relative z-10">
                {/* Top specular edge reflection */}
                <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none rounded-t-3xl z-10" />

                <div className="p-5 sm:p-7 md:p-8">
                    
                    {busyUntil ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h4 className="text-xl font-bold text-white mb-2">Domain is Busy</h4>
                            <p className="text-neutral-400 mb-6">The domain <span className="text-primary-400 font-semibold">{domain?.name}</span> is currently occupied until {busyUntil.toLocaleDateString()}. Notify me when it becomes available?</p>
                            <a 
                                href={process.env.REACT_APP_TELEGRAM_CONTACT || (process.env.REACT_APP_TELEGRAM_BOT_USERNAME ? `https://t.me/${process.env.REACT_APP_TELEGRAM_BOT_USERNAME}?start=notify_${domain?.id}` : '#')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-6 py-2.5 bg-neutral-900 hover:bg-neutral-800 text-white font-medium rounded-xl transition-colors border border-white/[0.12]"
                            >
                                Notify me
                            </a>
                        </div>
                    ) : showPaymentOptions && invoiceData ? (
                        <PaymentModalView
                            domain={domain}
                            action="rent"
                            durationText={`${formData.rentalPeriod} month(s) rental`}
                            amount={invoiceData.amount}
                            invoiceData={{ ...invoiceData, duration: formData.rentalPeriod }}
                            telegramBotUsername={process.env.REACT_APP_TELEGRAM_BOT_USERNAME || ''}
                            destination={formData.aRecord}
                            onBack={() => { setShowPaymentOptions(false); setInvoiceData(null); }}
                        />
                    ) : submitSuccess ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h4 className="text-xl font-bold text-white mb-2">Payment Successful!</h4>
                            <p className="text-neutral-400">We'll contact you with setup details.</p>
                        </div>
                    ) : (
                        <>
                            <p className="text-neutral-300 mb-2">
                                Rent <span className="text-primary-400 font-semibold">{domain?.name}</span> for a specified period.
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-4 pt-1">
                                <div>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            id="rent_aRecord"
                                            name="aRecord"
                                            value={formData.aRecord}
                                            onChange={handleChange}
                                            placeholder=" "
                                            required
                                            className="peer block w-full px-3.5 py-2.5 bg-neutral-900/90 border border-white/[0.12] focus:border-white/40 focus:ring-1 focus:ring-white/30 rounded-xl text-white focus:outline-none transition-all placeholder-transparent text-sm"
                                        />
                                        <label
                                            htmlFor="rent_aRecord"
                                            className="absolute start-3 -top-2.5 z-10 px-1.5 text-xs text-neutral-400 bg-neutral-950 rounded pointer-events-none transition-all duration-200 transform origin-left peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2.5 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:text-white peer-focus:bg-neutral-950"
                                        >
                                            A Record (IP Address)
                                        </label>
                                    </div>
                                    <p className="text-xs text-neutral-400 mt-1.5 px-1">
                                        The IP address your domain will point to after payment
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-neutral-300 mb-1">
                                        Rental Period: {formData.rentalPeriod} month{formData.rentalPeriod !== 1 ? 's' : ''}
                                    </label>
                                    <input
                                        type="range"
                                        name="rentalPeriod"
                                        min="1"
                                        max="12"
                                        value={formData.rentalPeriod}
                                        onChange={handleChange}
                                        className="w-full accent-white"
                                    />
                                    <div className="flex justify-between text-xs text-neutral-400 mt-1">
                                        <span>1 month</span>
                                        <span>12 months</span>
                                    </div>
                                    <div className="text-center mt-2">
                                        {formData.rentalPeriod > 1 && domain.discount_rent > 0 && (
                                            <>
                                                <p className="text-xs text-neutral-500 line-through mb-1">
                                                    ${(basePrice * formData.rentalPeriod).toFixed(2)}
                                                </p>
                                                <p className="text-xs text-emerald-400">
                                                    Progressive discount applied! ({(((formData.rentalPeriod - 1) / 11) * Number(domain.discount_rent)).toFixed(1)}% off)
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {submitError && typeof submitError === 'string' && (
                                    <div className="text-red-400 text-sm text-center bg-red-400/10 p-3 rounded-xl mb-4">{submitError}</div>
                                )}
                                {submitError && typeof submitError === 'object' && (
                                    <div className="text-orange-400 text-sm text-center bg-orange-400/10 p-4 rounded-xl mb-4">
                                        <p className="mb-2">{submitError.message}</p>
                                        <a
                                            href={process.env.REACT_APP_TELEGRAM_CONTACT || (process.env.REACT_APP_TELEGRAM_BOT_USERNAME ? `https://t.me/${process.env.REACT_APP_TELEGRAM_BOT_USERNAME}?start=notify_${domain?.id}` : '#')}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-block mt-2 px-4 py-2 bg-neutral-900 hover:bg-neutral-800 text-white rounded-xl transition-colors border border-white/[0.12]"
                                        >
                                            Notify me
                                        </a>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isSubmitting || isGeneratingInvoice}
                                    className="w-full bg-gradient-to-r from-purple-500 via-purple-600 to-indigo-600 hover:from-purple-400 hover:via-purple-500 hover:to-indigo-500 text-white font-semibold py-3.5 px-5 rounded-xl transition-all duration-200 border border-purple-400/30 hover:border-purple-300/50 shadow-[0_10px_25px_-5px_rgba(168,85,247,0.35),0_4px_10px_rgba(0,0,0,0.4),inset_0_1px_1px_rgba(255,255,255,0.35)] active:scale-[0.99] flex items-center justify-center min-h-[52px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm font-bold"
                                >
                                    {isGeneratingInvoice ? 'Creating Invoice...' : isSubmitting ? 'Processing...' : `Continue to Payment $${currentPrice.toFixed(2)}`}
                                </button>
                            </form>
                        </>
                    )}
                </div>
                
                <FormSecurityNotice />
            </div>
        </div>
    );
};

export default RentForm;